// Action Creators
import { ActionCreator } from "@ms/planner-actioncreators/lib/local/actioncreators/ActionCreator";
// Constants
import { TraceLevel } from "@ms/uno-telemetry/lib/local/events/Trace.event";
// Contexts
import { ActionCreatorLoaderContext } from "@ms/planner-container-controls/lib/local/contexts/ActionCreatorLoaderContext";
import { AgentProviderContext } from "@ms/planner-container-controls/lib/local/contexts/AgentProviderContext";
import { ConfigProviderContext } from "@ms/planner-container-controls/lib/local/contexts/ConfigProviderContext";
import { StoreLoaderContext } from "@ms/planner-container-controls/lib/local/contexts/StoreLoaderContext";
import { StoreSubscriptionManagerContext } from "@ms/planner-container-controls/lib/local/contexts/StoreSubscriptionManagerContext";
// Controls
import { AriaLiveAnnouncer, Spinner } from "@fluentui/react-components";
import { LazyCreateTaskFromMessageContainer } from "@ms/planner-container-controls/lib/local/controls/createTaskFromMessage/CreateTaskFromMessageLazyLoader";
import { LazyNotificationBarContainer } from "@ms/planner-container-controls/lib/local/controls/notifications/LazyNotificationBarContainer";
// Hooks
import { useStores } from "@ms/planner-container-controls/lib/local/hooks/useStores";
import { LoggerContextProvider } from "@ms/planner-presentational-controls/lib/local/utilities/contexts/LoggerContext";
import { PlannerThemeProvider } from "@ms/planner-presentational-controls/lib/local/utilities/contexts/PlannerThemeProvider";
import { Store } from "@ms/planner-stores/lib/local/stores/Store";
// Resources
import { Strings, loadTranslations } from "@ms/uno-resources/lib/local/CultureUtilities";
// Styles
import { useGlobalScrollbarStyles } from "../shellControls/PlannerShellLayout.styles";
import { useCreateTaskFromMessageShellStyles } from "./CreateTaskFromMessageShell.styles";
// Utilities
import { initializeAndRegisterIcons } from "@ms/uno-utilities/lib/local/ReactUtilities";
import { default as React, Suspense, useCallback, useEffect } from "react";
/**
 * Internal implementation of CreateTaskFromMessageShell
 */ const CreateTaskFromMessageShellInternal = (props)=>{
    const { onLoad, onSuccess, hostAdaptor, loggers, acLoader } = props;
    const styles = useCreateTaskFromMessageShellStyles();
    useGlobalScrollbarStyles();
    const [appContextStore] = useStores([
        Store.AppContext
    ]);
    /**
     * Update current user in user store
     */ const updateCurrentUserInStore = useCallback(async ()=>{
        const appContext = appContextStore.getAppContext();
        const userId = appContext.userId;
        if (userId == null) {
            const msg = "This should never happen. UserId retrieved from app context is null";
            loggers.traceLogger.logTrace(0x1e0e26c1 /* tag_4d81b */ , TraceLevel.Error, msg);
            throw new Error(msg);
        }
        acLoader.load(ActionCreator.User).fetchUsers([
            userId
        ], true);
    }, [
        acLoader,
        appContextStore,
        loggers.traceLogger
    ]);
    // Initialize app resources and basic setup
    // TODO : 2824315 : [EVO] Move updateCurrentUserInStore  in PlannerShell and CreateTaskFromMessageShell
    useEffect(()=>{
        // Initialize resource localization
        loadTranslations(appContextStore.getAppContext().locale);
        if (hostAdaptor.canSetDocumentTitle()) {
            document.title = Strings.LandingPage_PlannerTitle;
        }
        loggers.traceLogger.logTrace(0x1e0e26c0 /* tag_4d81a */ , TraceLevel.Info, "CreateTaskFromMessageShell component notifying load.");
        onLoad();
        loggers.traceLogger.logTrace(0x1e0e26a3 /* tag_4d809 */ , TraceLevel.Info, "CreateTaskFromMessageShell initialization complete. Notifying success.");
        onSuccess();
        // Run side effects
        updateCurrentUserInStore();
        initializeAndRegisterIcons();
    }, [
        appContextStore,
        hostAdaptor,
        loggers.traceLogger,
        onLoad,
        onSuccess,
        updateCurrentUserInStore
    ]);
    /**
     * Renders a loading spinner
     */ const renderLoadingSpinner = ()=>{
        return /*#__PURE__*/ React.createElement(Spinner, {
            size: "large",
            className: styles.spinner
        });
    };
    const renderNotificationBar = ()=>{
        return /*#__PURE__*/ React.createElement(Suspense, {
            fallback: /*#__PURE__*/ React.createElement(React.Fragment, null)
        }, /*#__PURE__*/ React.createElement(LazyNotificationBarContainer, null));
    };
    /**
     * Renders the basic plan view
     */ const renderCreateTaskFromMessage = ()=>{
        return /*#__PURE__*/ React.createElement(Suspense, {
            fallback: renderLoadingSpinner()
        }, /*#__PURE__*/ React.createElement(LazyCreateTaskFromMessageContainer, null), /*#__PURE__*/ React.createElement("div", {
            className: styles.notificationBarContainer
        }, renderNotificationBar()));
    };
    const appContext = appContextStore.getAppContext();
    const currentTheme = appContext.theme;
    return /*#__PURE__*/ React.createElement(PlannerThemeProvider, {
        theme: currentTheme
    }, /*#__PURE__*/ React.createElement(AriaLiveAnnouncer, null, /*#__PURE__*/ React.createElement("div", {
        className: styles.root
    }, renderCreateTaskFromMessage())));
};
/**
 * CreateTaskFromMessageShell component
 */ export const CreateTaskFromMessageShell = (props)=>{
    return /*#__PURE__*/ React.createElement(StoreSubscriptionManagerContext.Provider, {
        value: props.dispatcher
    }, /*#__PURE__*/ React.createElement(ConfigProviderContext.Provider, {
        value: props.configProvider
    }, /*#__PURE__*/ React.createElement(LoggerContextProvider, {
        value: props.loggers
    }, /*#__PURE__*/ React.createElement(StoreLoaderContext.Provider, {
        value: props.storeLoader
    }, /*#__PURE__*/ React.createElement(ActionCreatorLoaderContext.Provider, {
        value: props.acLoader
    }, /*#__PURE__*/ React.createElement(AgentProviderContext.Provider, {
        value: props.agentProviders
    }, /*#__PURE__*/ React.createElement(CreateTaskFromMessageShellInternal, props)))))));
};
